import { useTranslation } from "react-i18next";
import React from "react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { PrimaryButton, SecondaryButton } from "../Button";
import {
  DragHandle,
  SubtleDropPlaceholder,
  projectItemsWithPlaceholder,
  useBoardDnD,
  useSortableItem,
} from "../utils/PragmaticDragAndDrop";

import { useForm } from "react-hook-form";
import classNames from "../utils/classnames";
import { ChecklistItemState, State, useChecklistState } from "./useChecklistState";

import { Checkbox } from "../Checkbox";
import { Textfield } from "../FormElements/Textfield";
import { PieChart } from "../PieChart";
import { SwitchToggle } from "../SwitchToggle";
import { createTestId } from "../TestableElement";

export namespace Checklist {
  export type ChecklistItem = {
    id: string;
    name: string;
    completed: boolean;
    index: number;
    mode: "view" | "edit" | "delete";
  };

  export type AddChecklistItemFn = (inputs: { name: string }) => Promise<{
    success: boolean;
    id: string;
  }>;

  export type DeleteChecklistItemFn = (id: string) => Promise<boolean>;

  export type UpdateChecklistItemFn = (inputs: { itemId: string; name: string }) => Promise<boolean>;

  export type ToggleChecklistItemFn = (id: string, completed: boolean) => Promise<boolean>;
  export type UpdateChecklistItemIndexFn = (id: string, index: number) => Promise<boolean>;

  export interface Props {
    items: ChecklistItem[];
    canEdit: boolean;

    addItem: AddChecklistItemFn;
    deleteItem: DeleteChecklistItemFn;
    updateItem: UpdateChecklistItemFn;
    toggleItem: ToggleChecklistItemFn;
    updateItemIndex: UpdateChecklistItemIndexFn;

    sectionTitle?: string;
    sectionTitleBottomMargin?: string;
    togglable?: boolean;
    disabled?: boolean;
  }

  export interface InternalProps {
    items: ChecklistItem[];

    showEditButton?: boolean;
    togglable?: boolean;
    disabled?: boolean;

    addActive?: boolean;
    onAddActiveChange?: (active: boolean) => void;

    addItem: AddChecklistItemFn;
    deleteItem: DeleteChecklistItemFn;
    updateItem: UpdateChecklistItemFn;
    toggleItem: ToggleChecklistItemFn;
    updateItemIndex: UpdateChecklistItemIndexFn;
  }
}

export function Checklist(props: Checklist.Props) {
  const { t } = useTranslation();
  const [addActive, setAddActive] = React.useState(false);
  const completedCount = props.items.filter((item) => item.completed).length;
  const totalCount = props.items.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const sectionTitle = props.sectionTitle || t("Checklist");
  const sectionTitlBottomMargin = props.sectionTitleBottomMargin || "mb-3";
  const togglable = props.togglable ?? true;
  const disabled = props.disabled ?? false;

  return (
    <div>
      <ChecklistSectionHeader
        title={sectionTitle}
        completionPercentage={completionPercentage}
        completedCount={completedCount}
        totalCount={totalCount}
        canEdit={props.canEdit}
        togglable={togglable}
        addActive={addActive}
        onAddClick={() => setAddActive(true)}
      />

      {Boolean(!addActive && props.items.length === 0) && (
        <div className="mt-1">
          <div className="text-content-dimmed text-sm">
            {props.canEdit
              ? t("Create a checklist to track qualitative progress or binary outcomes.")
              : t("This goal doesn't have a checklist.")}
          </div>
        </div>
      )}

      <div className={sectionTitlBottomMargin}>
        <ChecklistInternal
          items={props.items}
          showEditButton={props.canEdit}
          togglable={togglable}
          disabled={disabled}
          addActive={addActive}
          onAddActiveChange={setAddActive}
          addItem={props.addItem}
          deleteItem={props.deleteItem}
          updateItem={props.updateItem}
          toggleItem={props.toggleItem}
          updateItemIndex={props.updateItemIndex}
        />
      </div>
    </div>
  );
}

function ChecklistSectionHeader({
  completionPercentage,
  completedCount,
  totalCount,
  canEdit,
  togglable,
  addActive,
  onAddClick,
  title,
}: {
  completionPercentage: number;
  completedCount: number;
  totalCount: number;
  canEdit: boolean;
  togglable: boolean;
  addActive: boolean;
  onAddClick: () => void;
  title: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <h2 className="font-semibold">{title}</h2>
        {totalCount > 0 && (
          <div className="flex items-center gap-2">
            <PieChart size={20} slices={[{ percentage: completionPercentage, color: "var(--color-green-500)" }]} />
            <span className="text-sm text-content-dimmed">
              {t("{{completed}}/{{total}} completed ({{percentage}}%)", {
                completed: completedCount,
                total: totalCount,
                percentage: completionPercentage,
              })}
            </span>
          </div>
        )}
        {canEdit && !addActive && togglable && (
          <SecondaryButton size="xxs" onClick={onAddClick} testId="add-checklist-item">
            {t("Add")}
          </SecondaryButton>
        )}
      </div>
    </div>
  );
}

function ChecklistInternal(props: Checklist.InternalProps) {
  const state = useChecklistState(props);

  return <ChecklistItemList state={state} />;
}

function ChecklistItemList({ state }: { state: State }) {
  const containerId = React.useId();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const sorted = React.useMemo(() => {
    return [...state.items].sort((a, b) => a.index - b.index);
  }, [state.items]);

  const { draggedItemId, destination, draggedItemDimensions } = useBoardDnD(({ itemId, destination }) => {
    if (state.togglable && !state.disabled) {
      state.reorder(itemId, destination.index);
    }
  });

  const { items: projectedItems, placeholderIndex } = React.useMemo(
    () =>
      projectItemsWithPlaceholder({
        items: sorted,
        getId: (item) => item.id,
        draggedItemId,
        targetLocation: destination,
        containerId,
      }),
    [containerId, destination, draggedItemId, sorted],
  );

  const itemsWithIndex = React.useMemo(() => {
    return projectedItems.map((item, index) => ({ ...item, index }));
  }, [projectedItems]);

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        containerId,
        index: itemsWithIndex.length,
      }),
    });
  }, [containerId, itemsWithIndex.length]);

  const placeholderHeight = draggedItemDimensions?.height ?? null;

  return (
    <div ref={containerRef} className="space-y-0">
      {itemsWithIndex.map((item, index) => (
        <React.Fragment key={item.id}>
          {placeholderIndex === index && (
            <SubtleDropPlaceholder containerId={containerId} index={index} height={placeholderHeight} />
          )}
          <div>
            {index === 0 && <div className="h-2" />}
            <ChecklistItemCard state={state} item={item} containerId={containerId} />
            <div className="h-2" />
          </div>
        </React.Fragment>
      ))}
      {placeholderIndex !== null && placeholderIndex === itemsWithIndex.length && (
        <SubtleDropPlaceholder containerId={containerId} index={itemsWithIndex.length} height={placeholderHeight} />
      )}

      {state.addActive && <ChecklistItemAdd state={state} />}
    </div>
  );
}

function ChecklistItemCard({
  state,
  item,
  containerId,
}: {
  state: State;
  item: ChecklistItemState;
  containerId: string;
}) {
  if (item.mode === "view") {
    return <ChecklistItemView state={state} item={item} containerId={containerId} />;
  }

  if (item.mode === "edit") {
    return <ChecklistItemEdit state={state} item={item} />;
  }

  throw new Error(`Unknown mode: ${item.mode}`);
}

function ChecklistItemAdd({ state }: { state: State }) {
  const { t } = useTranslation();
  const nameValidation = useChecklistNameValidation();
  const [createMore, setCreateMore] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data: { name: string }) => {
    const result = await state.addItem({
      name: data.name,
    });

    if (result) {
      if (createMore) {
        reset();
      } else {
        state.cancelAdd();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    } else if (e.key === "Escape") {
      e.preventDefault();
      state.cancelAdd();
    }
  };

  return (
    <InlineModal index={100}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Textfield
          testId="checklist-item-name"
          label={t("Check")}
          autoFocus
          placeholder={t("e.g. Sign the contract")}
          error={errors.name?.message}
          onKeyDown={handleKeyDown}
          {...register("name", nameValidation)}
        />
        <div className="flex items-center mt-4">
          <SwitchToggle value={createMore} setValue={setCreateMore} label={t("Create more")} />
          <div className="flex-1"></div>
          <div className="flex gap-2">
            <SecondaryButton size="xs" onClick={() => state.cancelAdd()} type="button" testId="cancel">
              {t("Cancel")}
            </SecondaryButton>
            <PrimaryButton size="xs" type="submit" testId="save">
              {t("Add Check")}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </InlineModal>
  );
}

function ChecklistItemEdit({ state, item }: { state: State; item: ChecklistItemState }) {
  const { t } = useTranslation();
  const nameValidation = useChecklistNameValidation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: item.name,
    },
  });

  const onSubmit = (data: { name: string }) => {
    state.saveEdit(item.id, {
      name: data.name,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <InlineModal index={item.index}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Textfield
          label={t("Check")}
          error={errors.name?.message}
          onKeyDown={handleKeyDown}
          {...register("name", nameValidation)}
        />
        <div className="flex items-center gap-2 justify-end mt-4">
          <SecondaryButton size="xs" onClick={() => state.cancelEdit(item.id)} type="button">
            {t("Cancel")}
          </SecondaryButton>
          <PrimaryButton size="xs" type="submit" testId="save">
            {t("Save")}
          </PrimaryButton>
        </div>
      </form>
    </InlineModal>
  );
}

function useChecklistNameValidation() {
  const { t } = useTranslation();

  return {
    validate: (name: string) => {
      if (!name.trim()) return t("Can't be empty");
      // Match PostgreSQL's character limit, including emoji and combining characters.
      return Array.from(name).length <= 255 || t("Must be at most {{maxLength}} characters long", { maxLength: 255 });
    },
  };
}

function InlineModal({ index, children }: { index: number; children: React.ReactNode }) {
  const outerClass = "border-b border-stroke-base";

  const innerClass = classNames("border border-surface-outline rounded-lg p-8 px-6 shadow-lg", {
    "mb-4": index === 0,
    "my-4": index !== 0,
  });

  return (
    <div className={outerClass}>
      <div className={innerClass}>{children}</div>
    </div>
  );
}

function ChecklistItemView({
  state,
  item,
  containerId,
}: {
  state: State;
  item: ChecklistItemState;
  containerId: string;
}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const { ref, dragHandleRef, isDragging } = useSortableItem({
    itemId: item.id,
    index: item.index,
    containerId,
    disabled: !state.togglable || state.disabled,
  });

  const handleCheckboxChange = () => {
    if (state.togglable) {
      state.toggleItem(item.id, !item.completed);
    }
  };

  const groupClass = classNames("group relative flex items-start gap-2", {
    "hover:bg-surface-highlight transition-colors": state.togglable,
    "opacity-40": isDragging,
  });

  return (
    <div
      className={groupClass}
      ref={ref as React.RefObject<HTMLDivElement>}
      data-test-id={createTestId("checklist-item", item.name)}
    >
      <div ref={dragHandleRef as React.RefObject<HTMLDivElement>}>
        <DragHandle
          isDragging={isDragging}
          disabled={!state.togglable || state.disabled}
          className="absolute -left-5 mt-0.5"
        />
      </div>
      <Checkbox
        checked={item.completed}
        onChange={handleCheckboxChange}
        disabled={!state.togglable}
        className="mt-0.5"
        testId={createTestId("checkbox", item.name)}
      />
      <ChecklistItemName item={item} />
      {item.editButtonVisible && state.togglable && (
        <ChecklistItemEditButton state={state} item={item} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}
    </div>
  );
}

function ChecklistItemName({ item }: { item: ChecklistItemState }) {
  const className = classNames("flex-1 font-medium", {
    "line-through text-content-dimmed": item.completed,
  });

  return <div className={className}>{item.name}</div>;
}

function ChecklistItemEditButton({
  state,
  item,
  isMenuOpen,
  setIsMenuOpen,
}: {
  state: State;
  item: ChecklistItemState;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="opacity-0 group-hover:opacity-100 transition-opacity relative">
      <button
        className="menu-button p-1.5 text-content-dimmed hover:text-content-base hover:bg-surface-dimmed rounded-full"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        data-test-id={createTestId("checklist-item-menu", item.id)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="3" r="1" fill="currentColor" />
          <circle cx="8" cy="8" r="1" fill="currentColor" />
          <circle cx="8" cy="13" r="1" fill="currentColor" />
        </svg>
      </button>

      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
          <div className="absolute right-0 top-8 z-20 bg-surface-base rounded-md shadow-lg ring-1 ring-surface-outline py-1 w-20">
            <button
              className="block w-full text-left px-3 py-1 text-sm text-content-dimmed hover:bg-surface-highlight hover:text-content-base"
              onClick={() => {
                state.startEdit(item.id);
                setIsMenuOpen(false);
              }}
              data-test-id="edit"
            >
              {t("Edit")}
            </button>
            <button
              className="block w-full text-left px-3 py-1 text-sm text-content-error hover:bg-surface-highlight"
              onClick={() => {
                state.deleteItem(item.id);
                setIsMenuOpen(false);
              }}
              data-test-id="delete"
            >
              {t("Delete")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
